import React, { Component, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { Box, ThemeProvider, CssBaseline } from '@mui/material';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import Home from './components/Home';
import Services from './components/Services';
import Faqs from './components/Faqs';
import Policies from './components/Policies';
import About from './components/About';
import Contact from './components/Contact';
import ScrollToTop from './components/helpers/ScrollToTop';
import theme from './theme';
import seoContent from './content/seo.json';
import './styles.css';

const ensureMetaTag = (selector, attributes) => {
    let tag = document.head.querySelector(selector);

    if (!tag) {
        tag = document.createElement('meta');
        document.head.appendChild(tag);
    }

    Object.entries(attributes).forEach(([key, value]) => {
        tag.setAttribute(key, value);
    });

    return tag;
};

const ensureLinkTag = (selector, attributes) => {
    let tag = document.head.querySelector(selector);

    if (!tag) {
        tag = document.createElement('link');
        document.head.appendChild(tag);
    }

    Object.entries(attributes).forEach(([key, value]) => {
        tag.setAttribute(key, value);
    });

    return tag;
};

const ensureStructuredDataScript = (id, json) => {
    let script = document.getElementById(id);

    if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(json);
};

const SEOController = () => {
    const location = useLocation();

    useEffect(() => {
        const pathname = location.pathname || '/';
        const {
            siteName,
            browserTitle,
            canonicalBaseUrl,
            defaultImagePath,
            robots,
            defaultTitle,
            defaultDescription,
            routes,
            structuredData
        } = seoContent;

        const routeMeta = routes[pathname] || {
            title: defaultTitle,
            description: defaultDescription
        };

        const canonicalUrl = `${canonicalBaseUrl}${pathname === '/' ? '/' : pathname}`;
        const defaultImageUrl = `${canonicalBaseUrl}${defaultImagePath}`;

        document.title = browserTitle;
        document.documentElement.setAttribute('lang', 'en');

        ensureMetaTag('meta[name="description"]', {
            name: 'description',
            content: routeMeta.description
        });

        ensureMetaTag('meta[name="robots"]', {
            name: 'robots',
            content: robots
        });

        ensureMetaTag('meta[property="og:title"]', {
            property: 'og:title',
            content: routeMeta.title
        });

        ensureMetaTag('meta[property="og:description"]', {
            property: 'og:description',
            content: routeMeta.description
        });

        ensureMetaTag('meta[property="og:type"]', {
            property: 'og:type',
            content: 'website'
        });

        ensureMetaTag('meta[property="og:url"]', {
            property: 'og:url',
            content: canonicalUrl
        });

        ensureMetaTag('meta[property="og:site_name"]', {
            property: 'og:site_name',
            content: siteName
        });

        ensureMetaTag('meta[property="og:image"]', {
            property: 'og:image',
            content: defaultImageUrl
        });

        ensureMetaTag('meta[name="twitter:card"]', {
            name: 'twitter:card',
            content: 'summary'
        });

        ensureMetaTag('meta[name="twitter:title"]', {
            name: 'twitter:title',
            content: routeMeta.title
        });

        ensureMetaTag('meta[name="twitter:description"]', {
            name: 'twitter:description',
            content: routeMeta.description
        });

        ensureMetaTag('meta[name="twitter:image"]', {
            name: 'twitter:image',
            content: defaultImageUrl
        });

        ensureLinkTag('link[rel="canonical"]', {
            rel: 'canonical',
            href: canonicalUrl
        });

        const resolvedStructuredData = {
            ...structuredData,
            url: `${canonicalBaseUrl}/`,
            logo: defaultImageUrl,
            image: defaultImageUrl
        };

        ensureStructuredDataScript('site-structured-data', resolvedStructuredData);
    }, [location]);

    return null;
};

export class App extends Component {
    render() {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box
                    className="site-shell"
                    sx={{
                        minHeight: '100vh',
                        backgroundColor: '#f3e8ed',
                        backgroundImage: `
        linear-gradient(rgba(239, 230, 235, 0.72), rgba(239, 230, 235, 0.72)),
        url('/img/webbackgroundnew.webp')
    `,
                        backgroundRepeat: 'repeat-y',
                        backgroundPosition: 'top center',
                        backgroundSize: { xs: '1600px auto', md: '2100px auto' },
                        backgroundAttachment: 'fixed'
                    }}
                >
                    <Router>
                        <SEOController />
                        <ScrollToTop />
                        <Navigation />
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/faqs" element={<Faqs />} />
                            <Route path="/policies" element={<Policies />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/contact" element={<Contact />} />
                        </Routes>
                        <Footer />
                    </Router>
                </Box>
            </ThemeProvider>
        );
    }
}

export default App;